import { useEffect, useRef, useState } from 'react';
import {
  LayoutDashboard,
  Users,
  CalendarDays,
  Calendar,
  Wallet,
  MessageSquare,
  Video,
} from 'lucide-react';
import { MantaRayIcon } from '@/components/icons/MantaRayIcon';

const CONTENT_WIDTH = 782;
const CONTENT_HEIGHT = 820;
const CHROME_H = 44;
const NAVBAR_H = 56;
const BODY_H = CONTENT_HEIGHT - CHROME_H - NAVBAR_H;

const NAV_ITEMS = [
  { icon: LayoutDashboard, label: 'Dashboard', active: true },
  { icon: Users, label: 'Students' },
  { icon: CalendarDays, label: 'Sessions' },
  { icon: Calendar, label: 'Calendar' },
  { icon: Wallet, label: 'Payments' },
  { icon: MessageSquare, label: 'Messages' },
];

const STUDENTS = [
  { name: 'Alex M.', next: 'Tomorrow 3pm', subjects: 'Math, Algebra' },
  { name: 'Jamie L.', next: 'Thu 4pm', subjects: 'SAT Prep' },
  { name: 'Ryan K.', next: 'Fri 2pm', subjects: 'Physics' },
];

export function TutorDashboardMockup() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.6);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setScale(el.offsetWidth / CONTENT_WIDTH);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div>
      <div>
        <div
          ref={containerRef}
          className="w-full rounded-xl overflow-hidden shadow-2xl border border-border"
          style={{ aspectRatio: `${CONTENT_WIDTH} / ${CONTENT_HEIGHT}` }}
        >
          <div
            style={{
              width: `${CONTENT_WIDTH}px`,
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              height: `${CONTENT_HEIGHT}px`,
            }}
          >
            {/* Browser chrome */}
            <div
              className="flex items-center gap-3 px-4 bg-muted dark:bg-zinc-900 border-b border-border"
              style={{ height: `${CHROME_H}px` }}
            >
              <div className="flex gap-1.5 shrink-0">
                <span className="h-3 w-3 rounded-full bg-red-500/80" />
                <span className="h-3 w-3 rounded-full bg-yellow-500/80" />
                <span className="h-3 w-3 rounded-full bg-green-500/80" />
              </div>
              <div className="flex-1 flex justify-center">
                <div className="bg-background dark:bg-zinc-800 border border-border rounded-md px-4 py-1 text-[11px] text-muted-foreground w-56 text-center">
                  mentaratutors.com/dashboard
                </div>
              </div>
            </div>

            {/* In-app Navbar */}
            <div
              className="flex items-center px-6 bg-background border-b border-border"
              style={{ height: `${NAVBAR_H}px` }}
            >
              <div className="flex items-center gap-2">
                <MantaRayIcon className="h-10 w-10" />
                <span className="text-xl font-bold tracking-tight text-rose-900 dark:text-rose-300">
                  Mentara
                </span>
              </div>
            </div>

            {/* Dashboard body */}
            <div
              className="bg-background text-foreground overflow-hidden flex"
              style={{ height: `${BODY_H}px` }}
            >
              {/* Left sidebar */}
              <div className="w-44 shrink-0 border-r border-border bg-muted/30 py-4 flex flex-col gap-1">
                {NAV_ITEMS.map(({ icon: Icon, label, active }) => (
                  <div
                    key={label}
                    className={`flex items-center gap-2.5 px-4 py-2 mx-2 rounded-md text-sm font-medium cursor-default ${
                      active
                        ? 'bg-background text-foreground shadow-sm border border-border'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    <Icon className="h-4 w-4 shrink-0" />
                    {label}
                  </div>
                ))}
              </div>

              {/* Main content */}
              <div className="flex-1 overflow-hidden">
                <div
                                    className="px-8 py-6 space-y-5"
                >
                  {/* Welcome header */}
                  <div>
                    <p className="text-2xl font-bold tracking-tight">Welcome back, Sarah! 👋</p>
                    <p className="text-sm text-muted-foreground">Here's an overview of your activity</p>
                  </div>

                  {/* Stats row */}
                  <div className="grid grid-cols-3 gap-3">
                    <div className="rounded-lg border border-border bg-card p-4">
                      <p className="text-2xl font-bold">12</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Active Students</p>
                    </div>
                    <div className="rounded-lg border border-border bg-card p-4">
                      <p className="text-2xl font-bold">5</p>
                      <p className="text-xs text-muted-foreground mt-0.5">Sessions This Week</p>
                    </div>
                    <div className="rounded-lg border border-emerald-200 dark:border-emerald-900 bg-gradient-to-br from-emerald-50 dark:from-emerald-950 to-background p-4">
                      <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-400">$840</p>
                      <p className="text-xs text-muted-foreground mt-0.5">This Month</p>
                    </div>
                  </div>

                  {/* Your Students */}
                  <div className="rounded-lg border border-border bg-card overflow-hidden">
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-sm font-semibold">Your Students</p>
                    </div>
                    <div className="divide-y divide-border">
                      {STUDENTS.map((s) => (
                        <div key={s.name} className="flex items-center gap-3 px-4 py-3">
                          <div className="h-7 w-7 rounded-full bg-rose-100 dark:bg-rose-900/30 flex items-center justify-center text-[11px] font-semibold text-rose-700 dark:text-rose-300 shrink-0">
                            {s.name[0]}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium">{s.name}</p>
                            <p className="text-xs text-muted-foreground">Next: {s.next}</p>
                          </div>
                          <span className="text-xs text-muted-foreground shrink-0">{s.subjects}</span>
                          <div className="flex items-center gap-1 border border-border text-muted-foreground text-xs px-2.5 py-1 rounded-md shrink-0 cursor-default">
                            <MessageSquare className="h-3 w-3" />
                            Message
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Upcoming Sessions */}
                  <div className="rounded-lg border border-border bg-card overflow-hidden">
                    <div className="px-4 py-3 border-b border-border">
                      <p className="text-sm font-semibold">Upcoming Sessions</p>
                    </div>
                    <div className="p-3 space-y-2">
                      {/* Today — joinable */}
                      <div className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5 border-l-4 border-l-emerald-500">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold">Alex M.: Algebra Review</p>
                          <p className="text-xs text-muted-foreground">Today 4:00 PM</p>
                        </div>
                        <div className="flex items-center gap-1 bg-emerald-600 text-white text-xs font-medium px-2.5 py-1 rounded-md shrink-0 cursor-default">
                          <Video className="h-3 w-3" />
                          Join Session
                        </div>
                      </div>
                      {/* Tomorrow */}
                      <div className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5 border-l-4 border-l-blue-400">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold">Jamie L.: SAT Math</p>
                          <p className="text-xs text-muted-foreground">Tomorrow 3:00 PM</p>
                        </div>
                        <div className="flex items-center gap-1 border border-border text-muted-foreground text-xs px-2.5 py-1 rounded-md shrink-0 cursor-default">
                          View
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
